import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MediaGalleryResponse, MediaFile } from './useMediaGallery';
import { auth } from '../config/firebase';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.socialness.ai';

export interface UseMediaApiProps {
  userId?: string;
  brandId?: string;
}

export const useMediaApi = ({ userId, brandId }: UseMediaApiProps = {}) => {
  const queryClient = useQueryClient();
  
  // Get current user ID from Firebase auth if not provided
  const getCurrentUserId = async (): Promise<string> => {
    if (userId) return userId;
    
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Not authenticated');
    }
    
    return currentUser.uid;
  };
  
  // Fetch media files
  const fetchMediaFiles = async (): Promise<MediaGalleryResponse> => {
    // Get the JWT token
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    
    // Call the correct endpoint
    const response = await fetch(`${API_URL}/storage/list`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch media files');
    }
    
    const rawData = await response.json();
    
    // Transform the API response to match our MediaFile interface
    const transformedFiles: MediaFile[] = rawData.files?.map((file: {
      key: string;
      size: number;
      lastModified: string;
      mediaType: string;
      publicUrl: string;
      fileName: string;
      format: string;
      formattedSize: string;
    }) => ({
      key: file.key,
      size: file.size,
      lastModified: file.lastModified,
      mediaType: file.mediaType,
      url: file.publicUrl, // Map publicUrl to url
      name: file.fileName,
      format: file.format,
      formattedSize: file.formattedSize
    })) || [];
    
    return {
      files: transformedFiles,
      prefix: rawData.userPath || '',
      totalFiles: rawData.totalCount || transformedFiles.length
    };
  };
  
  // Query hook for fetching media
  const useMediaQuery = (options = {}) => {
    return useQuery<MediaGalleryResponse>({
      queryKey: ['media', { userId, brandId }],
      queryFn: fetchMediaFiles,
      ...options
    });
  };
  
  // Upload media file
  const uploadMediaMutation = useMutation({
    mutationFn: async ({ 
      file, 
      brandId: fileBrandId 
    }: { 
      file: File, 
      brandId?: string 
    }) => {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      
      if (fileBrandId) {
        formData.append('brandId', fileBrandId);
      }
      
      const response = await fetch(`${API_URL}/storage/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload file');
      }
      
      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['media'] });
    }
  });
  
  // Delete media file
  const deleteMediaMutation = useMutation({
    mutationFn: async ({ key }: { key: string }) => {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Encode the fileName for URL parameter
      const encodedFileName = encodeURIComponent(key);
      console.log('Deleting file with key:', key);
      console.log('DELETE request URL:', `${API_URL}/storage/delete?fileName=${encodedFileName}`);
      
      try {
        // Use the correct DELETE endpoint with fileName query parameter
        const response = await fetch(`${API_URL}/storage/delete?fileName=${encodedFileName}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log('DELETE response status:', response.status);
        console.log('DELETE response headers:', Object.fromEntries([...response.headers.entries()]));
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Delete file API error:', response.status, errorText);
          throw new Error(`Failed to delete file: ${response.status} ${errorText}`);
        }
        
        const responseData = response.status === 204 ? { success: true } : await response.json();
        console.log('Delete file success for key:', key, 'Response:', responseData);
        return { success: true, key, response: responseData };
      } catch (error) {
        console.error('Network or parsing error during delete:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Delete file mutation successful:', data);
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
    onError: (error) => {
      console.error('Delete file mutation error:', error);
    }
  });
  
  // Bulk delete media files
  const bulkDeleteMediaMutation = useMutation({
    mutationFn: async ({ fileNames }: { fileNames: string[] }) => {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      console.log('Calling bulkDelete API with fileNames:', fileNames);
      
      // Create request body with original file names
      const requestBody = { fileNames };
      console.log('Request body:', JSON.stringify(requestBody));
      console.log('Bulk DELETE request URL:', `${API_URL}/storage/bulkDelete`);
      
      try {
        const response = await fetch(`${API_URL}/storage/bulkDelete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        });
        
        console.log('Bulk DELETE response status:', response.status);
        console.log('Bulk DELETE response headers:', Object.fromEntries([...response.headers.entries()]));
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Bulk delete API error:', response.status, errorText);
          throw new Error(`Failed to bulk delete files: ${response.status} ${errorText}`);
        }
        
        const result = await response.json();
        console.log('Bulk delete success:', result);
        
        return { success: true, fileNames, result };
      } catch (error) {
        console.error('Network or parsing error during bulk delete:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Bulk delete mutation successful:', data);
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
    onError: (error) => {
      console.error('Bulk delete mutation error:', error);
    }
  });
  
  // Copy media URL to clipboard
  const copyMediaUrl = async (url: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch (error) {
      console.error('Failed to copy URL:', error);
      return false;
    }
  };
  
  // Handle file download
  const downloadMedia = (file: MediaFile) => {
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = file.url;
    
    // Extract filename from key
    const keyParts = file.key.split('/');
    const fileName = keyParts[keyParts.length - 1];
    
    // Set download attribute to suggest a filename
    link.download = fileName;
    
    // Append to the document
    document.body.appendChild(link);
    
    // Trigger the download
    link.click();
    
    // Clean up
    document.body.removeChild(link);
  };
  
  // Return all functions and mutations
  return {
    useMediaQuery,
    uploadMedia: uploadMediaMutation.mutate,
    uploadMediaAsync: uploadMediaMutation.mutateAsync,
    isUploading: uploadMediaMutation.isPending,
    uploadError: uploadMediaMutation.error,
    
    deleteMedia: deleteMediaMutation.mutate,
    deleteMediaAsync: deleteMediaMutation.mutateAsync,
    isDeleting: deleteMediaMutation.isPending,
    deleteError: deleteMediaMutation.error,
    
    bulkDeleteMedia: bulkDeleteMediaMutation.mutate,
    bulkDeleteMediaAsync: bulkDeleteMediaMutation.mutateAsync,
    isBulkDeleting: bulkDeleteMediaMutation.isPending,
    bulkDeleteError: bulkDeleteMediaMutation.error,
    
    copyMediaUrl,
    downloadMedia
  };
}; 